/**
 * API 客户端
 * 处理所有 API 调用的基础功能
 */

import CryptoJS from 'crypto-js';

const SECRET = 'your_secret_key'; // 与后端保持一致，建议用.env配置

function getSignString(data, timestamp) {
  // 对key排序，保证和后端一致
  const ordered = {};
  Object.keys(data).sort().forEach(key => {
    ordered[key] = data[key];
  });
  return JSON.stringify(ordered) + '|' + timestamp + '|' + SECRET;
}

function getSign(body) {
  const timestamp = Date.now().toString();
  const signString = getSignString(body, timestamp);
  const sign = CryptoJS.SHA256(signString).toString(CryptoJS.enc.Hex);
  return { sign, timestamp };
}

function buildUrlWithParams(url, params) {
  if (!params || Object.keys(params).length === 0) return url;
  const usp = new URLSearchParams(params);
  return url + (url.includes('?') ? '&' : '?') + usp.toString();
}

// API 基础 URL - 优化扩展环境适配
const getApiBaseUrl = () => {
  // 首先尝试从window对象获取（扩展环境）
  if (typeof window !== 'undefined' && window.REACT_APP_API_BASE_URL) {
    return window.REACT_APP_API_BASE_URL;
  }
  // 然后尝试从process.env获取（常规React环境）
  if (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }
  // 默认使用生产API（与前端同域，走 HTTPS）
  return 'https://www.aidengta.cn/api';
};

const API_BASE_URL = getApiBaseUrl();

/**
 * 创建请求配置对象
 * @param {Object} options 请求选项
 * @returns {Object} 完整的请求配置
 */
const createRequestConfig = (options = {}) => {
  const { method = 'GET', body, headers = {}, token, params = {}, endpoint } = options;

  // 解析 endpoint 里的 query string
  let urlParams = {};
  let pureEndpoint = endpoint;
  if (endpoint && endpoint.includes('?')) {
    const [base, queryStr] = endpoint.split('?');
    pureEndpoint = base;
    queryStr.split('&').forEach(pair => {
      const [key, value] = pair.split('=');
      if (key) urlParams[key] = decodeURIComponent(value || '');
    });
  }

  // 合并 params 和 urlParams，urlParams 优先
  const allParams = { ...params, ...urlParams };

  const config = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...headers
    }
  };

  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  let signData = {};
  const upperMethod = method.toUpperCase();
  if (["GET", "DELETE"].includes(upperMethod)) {
    signData = {};
    Object.keys(allParams).sort().forEach(key => {
      const val = allParams[key];
      if (val !== undefined && val !== '') {
        signData[key] = String(val);
      }
    });
  } else if (body && typeof body === 'object' && Object.keys(body).length > 0) {
    config.body = JSON.stringify(body);
    signData = body;
  } else if (Object.keys(allParams).length > 0) {
    signData = {};
    Object.keys(allParams).sort().forEach(key => {
      const val = allParams[key];
      if (val !== undefined && val !== '') {
        signData[key] = String(val);
      }
    });
  }

  // 重新拼接 url
  let url = pureEndpoint || endpoint;
  if (["GET", "DELETE"].includes(upperMethod) && Object.keys(allParams).length > 0) {
    const usp = new URLSearchParams(allParams);
    url = url + (url.includes('?') ? '&' : '?') + usp.toString();
  }
  config.url = url;

  const { sign, timestamp } = getSign(signData);
  config.headers['sign'] = sign;
  config.headers['timestamp'] = timestamp;

  return config;
};

// 全局登出触发器
function triggerLogout() {
  // 只触发全局事件，不直接清缓存
  window.dispatchEvent(new Event('force-logout'));
}

/**
 * 发送 API 请求
 * @param {string} endpoint API 端点
 * @param {Object} options 请求选项
 * @returns {Promise} 请求结果的 Promise
 */
export const apiRequest = async (endpoint, options = {}) => {
  let url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
  const { method = 'GET', params = {} } = options;

  // GET请求自动拼接params到URL
  if (method.toUpperCase() === 'GET' && params && Object.keys(params).length > 0) {
    url = buildUrlWithParams(url, params);
  }

  // 关键修正：将 endpoint 传入 createRequestConfig
  const config = createRequestConfig({ ...options, endpoint });
  
  try {
    // 在扩展环境中，需要确保CORS设置正确
    const response = await fetch(url, {
      ...config,
      mode: 'cors', // 显式设置CORS模式
      credentials: 'omit' // 扩展环境不需要发送cookies
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      const errorMsg = data.error || data.message || '请求失败';
      if (errorMsg.includes('身份验证失败') || errorMsg.includes('请重新登录')) {
        triggerLogout();
      }
      const error = new Error(errorMsg);
      error.statusCode = response.status;
      error.data = data;
      throw error;
    }
    
    return data;
  } catch (error) {
    if (!error.statusCode) {
      error.message = '网络错误，无法连接到服务器';
    }
    throw error;
  }
};

/**
 * GET 请求
 * @param {string} endpoint API 端点
 * @param {Object} options 请求选项
 * @returns {Promise} 请求结果的 Promise
 */
export const get = (endpoint, options = {}) => {
  return apiRequest(endpoint, {
    ...options,
    method: 'GET'
  });
};

/**
 * POST 请求
 * @param {string} endpoint API 端点
 * @param {Object} body 请求体数据
 * @param {Object} options 其他请求选项
 * @returns {Promise} 请求结果的 Promise
 */
export const post = (endpoint, body, options = {}) => {
  return apiRequest(endpoint, {
    ...options,
    method: 'POST',
    body
  });
};

/**
 * PUT 请求
 * @param {string} endpoint API 端点
 * @param {Object} body 请求体数据
 * @param {Object} options 其他请求选项
 * @returns {Promise} 请求结果的 Promise
 */
export const put = (endpoint, body, options = {}) => {
  return apiRequest(endpoint, {
    ...options,
    method: 'PUT',
    body
  });
};

/**
 * DELETE 请求
 * @param {string} endpoint API 端点
 * @param {Object} options 请求选项
 * @returns {Promise} 请求结果的 Promise
 */
export const del = (endpoint, options = {}) => {
  return apiRequest(endpoint, {
    ...options,
    method: 'DELETE'
  });
};

export default {
  get,
  post,
  put,
  delete: del,
  request: apiRequest
}; 